// @ts-strict-ignore
import * as React from 'react';
import {
    AppComponentProps,
    BucketRouteComponentMatchParams,
    CALLER_CONTEXT_PARAM,
    SetupStepsCallerContext,
} from '../../typescriptHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    BuildDistributionBucket,
    BucketStatus,
    Integration,
    IntegrationCapability,
    IntegrationType,
} from '../../types';
import Collapse from 'antd/lib/collapse/Collapse';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Search from 'antd/lib/input/Search';
import {
    CaretRightOutlined,
    InboxOutlined,
    ClockCircleOutlined,
    InfoCircleOutlined,
    ShareAltOutlined,
    ArrowRightOutlined,
    BlockOutlined,
    DeleteOutlined,
    UnorderedListOutlined,
    MenuOutlined,
    PlusOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import Button from 'antd/lib/button';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { tryFetchEntity } from '../../actions/entities';
import { FetchableEntityType } from '../../entityType';
import { buildBucketSort, shareLink, workflowsAndBranchesFromBuildDistroBucketRules } from '../../helpers';
import { globalMessage } from '../../utils/globalMessage';
import {
    selectAppSettings,
    selectIntegrationForCapability,
    selectSharedWithMeBuildIds,
    selectBuildDistributionBucketsForAppId,
    selectIncompleteCapabilitiesFromCapabilities,
    selectUserDevices,
    selectAppPlatform,
    selectShouldShowRegisterDevicesPrompt,
    selectAuthCredentials,
    selectIsDemoInstance,
    selectShouldShowSimplifiedBdUIForUser,
    selectUserIsAdmin,
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { useIsMobile } from '../../mediaQueriesHelpers';
import Divider from 'antd/lib/divider';
import { DesktopAndMobileHeader } from './DesktopAndMobileHeader';
import { CreateEditBuildBucketModal } from './CreateEditBuildBucketModal';
import Segmented from 'antd/lib/segmented';
import { Link, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import {
    createAppBuildDistroBucketRoute,
    createAppBuildsRoute,
    createBuildDistroBuildRoute,
} from '../../helpers/routeHelpers';
import {
    BuildBucketAvatar,
    BucketBuildsList,
    SharedWithMeBuildsList,
    BucketAccessIconTooltip,
    MultipleWorkflowsAndBranchesTag,
    BuildsList,
} from './CommonComponents';
import List from 'antd/lib/list';
import { formatDateWithTimezone } from '../../timeHelpers';
import { BuildBucketDetailsDrawer } from './mobileComponents/BuildBucketDetailsDrawer';
import routes from '../../routes';
import { BuildDetailsDrawer } from './BuildDetailsDrawer';
import { trackBuildDistroViewEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import {
    ConnectIntegrationsBannerWrapper,
    DefaultDocsLinkNode,
    CapabilityIntegrationsListItem,
} from '../ConnectIntegrationsBanner';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RegisterDeviceAlert } from '../RegisterDeviceAlert';
import { debounce } from 'lodash';
import { searchForBuildDistributionBuilds } from '@/api';
import Switch from 'antd/lib/switch';
import { tryUpdateShouldShowSimplifiedBdUI } from '@/actions/user';
import { UserUpdatable } from '@/actions/updatable';
import { shouldShowRegisterDevicesPromptForArtifactType } from '@/helpers/displayHelpers';
import { useError, useIsLoading } from '@/utils/hooks/helpers';

enum SegmentOptions {
    BuildBuckets = 'Build buckets',
    MostRecent = 'Most recent',
}

const iconForSegment = (segment: SegmentOptions, isMobile: boolean, isActive: boolean) => {
    const color = isActive ? runwayColors.primary : antGrey.grey6;

    switch (segment) {
        case SegmentOptions.BuildBuckets: {
            const Icon = isMobile ? UnorderedListOutlined : InboxOutlined;
            return (
                <Icon
                    style={{
                        color,
                    }}
                />
            );
        }
        case SegmentOptions.MostRecent: {
            const Icon = isMobile ? MenuOutlined : ClockCircleOutlined;
            return (
                <Icon
                    style={{
                        color,
                    }}
                />
            );
        }
    }
};

export interface BuildBucketRowBaseProps {
    isCollapsible: boolean;
    rowContent: React.ReactNode;
    extra?: React.ReactNode;
}

export const BuildBucketRowBase: React.FC<React.PropsWithChildren<BuildBucketRowBaseProps>> = ({
    isCollapsible,
    extra,
    rowContent,
    children,
}) => {
    return (
        <Collapse
            className="build-bucket-row"
            ghost
            expandIcon={({ isActive }) => (
                <CaretRightOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 14,
                    }}
                    rotate={isActive ? 90 : 0}
                />
            )}
            defaultActiveKey={isCollapsible ? ['1'] : []}
        >
            <Collapse.Panel
                collapsible={isCollapsible ? 'header' : 'disabled'}
                key="1"
                extra={extra}
                header={rowContent}
            >
                {children}
            </Collapse.Panel>
        </Collapse>
    );
};

export interface BuildBucketRowProps extends AppComponentProps {
    buildBucket: BuildDistributionBucket;
    imageUrl: string;
    buildSystemIntegration: Integration;
    vcsIntegrationType: IntegrationType;
    setSelectedBuildId: (buildId: string) => void;
}

export const BuildBucketRow: React.FC<BuildBucketRowProps> = ({
    buildBucket,
    buildSystemIntegration,
    vcsIntegrationType,
    appId,
    orgId,
    setSelectedBuildId,
}) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const matchesBucket = useRouteMatch<{
        bucketIdParam: string;
    }>([routes.appBucket]);

    const [isDrawerVisible, setIsDrawerVisible] = React.useState<boolean>(false);

    const shouldDrawerStartVisible = matchesBucket?.params.bucketIdParam === buildBucket.id;

    React.useEffect(() => {
        if (shouldDrawerStartVisible) {
            setIsDrawerVisible(true);
        }
    }, [shouldDrawerStartVisible]);

    const isMobile = useIsMobile();

    const bucketLink = createAppBuildDistroBucketRoute(orgId, appId, buildBucket.id);
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, appId).appImageUrl);

    return (
        <BuildBucketRowBase
            isCollapsible={true}
            extra={
                isMobile ?
                    <Button
                        icon={<InfoCircleOutlined />}
                        type="link"
                        onClick={() => {
                            history.push(bucketLink);
                        }}
                    />
                :   <Space>
                        <PermissionedActionButton
                            icon={<ShareAltOutlined />}
                            type="link"
                            onClick={() => {
                                const originalUrl = new URL(`${window.location.origin}${bucketLink}`);

                                const url = originalUrl.toString();

                                shareLink(url);

                                dispatch(
                                    trackBuildDistroViewEvent({
                                        orgId,
                                        appId,
                                        bucketId: buildBucket.id,
                                        eventName: AnalyticEvents.Event.BuildDistroClickShareBucketCta,
                                        extraProperties: {
                                            isPublicBucket: buildBucket.orgWideAccessEnabled,
                                            context: 'recentBuilds',
                                        },
                                    }),
                                );
                            }}
                        />
                        <Link to={bucketLink}>
                            <Button icon={<ArrowRightOutlined />} type="primary">
                                View bucket
                            </Button>
                        </Link>
                    </Space>
            }
            rowContent={
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ gap: isMobile ? 8 : 16, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                minWidth: 0,
                            }}
                        >
                            <div
                                style={{
                                    margin: isMobile ? 8 : '0 8px',
                                }}
                            >
                                <BuildBucketAvatar bucket={buildBucket} appImageUrl={appImageUrl} size={28} />
                            </div>
                            <BucketAccessIconTooltip
                                isExternalBucketView={buildBucket.publicBucketLinkEnabled}
                                bucketHasOrgWideAccess={buildBucket.orgWideAccessEnabled}
                            />
                            <Text
                                style={{
                                    fontSize: 16,
                                    fontWeight: 400,
                                    color: antGrey.grey8,
                                    minWidth: 0,
                                }}
                                ellipsis
                            >
                                <strong>{buildBucket.name}</strong> {`(${buildBucket.totalBuildsCount})`}
                            </Text>
                        </div>
                        {!isMobile && (
                            <MultipleWorkflowsAndBranchesTag
                                buildSystemIntegrationType={buildSystemIntegration?.id}
                                vcsIntegrationType={vcsIntegrationType}
                                workflowAndBranches={workflowsAndBranchesFromBuildDistroBucketRules(buildBucket.rules)}
                            />
                        )}
                    </div>
                </div>
            }
        >
            <BucketBuildsList
                appId={appId}
                onBuildSelection={(buildId) => {
                    setSelectedBuildId(buildId);
                }}
                orgId={orgId}
                buildIds={buildBucket.recentBuildIds}
                isMobile={isMobile}
                destinationURLFactory={(selectedBuildId, selectedBucketId) =>
                    createBuildDistroBuildRoute(orgId, appId, selectedBuildId, selectedBucketId)
                }
                leftInset
            />
            <BuildBucketDetailsDrawer
                open={isDrawerVisible}
                title={buildBucket.name}
                appId={appId}
                orgId={orgId}
                bucket={buildBucket}
                onClose={() => {
                    setIsDrawerVisible(false);
                }}
                afterOpenChange={(open) => {
                    if (!open) {
                        history.replace(createAppBuildsRoute(orgId, appId));
                    }
                }}
            />
        </BuildBucketRowBase>
    );
};

export interface SharedWithMeBucketProps extends AppComponentProps {
    buildsIds: string[];
    onBuildSelection?: (buildId: string) => void;
}

export const SharedWithMeBucket: React.FC<SharedWithMeBucketProps> = ({
    buildsIds,
    appId,
    orgId,
    onBuildSelection,
}) => {
    const isMobile = useIsMobile();

    return (
        <BuildBucketRowBase
            isCollapsible={true}
            rowContent={
                <Space>
                    <BlockOutlined style={{ color: antGrey.grey6, fontSize: 25, width: 52 }} />
                    <Text
                        style={{
                            fontSize: 16,
                            fontWeight: 400,
                            color: antGrey.grey8,
                        }}
                        ellipsis
                    >
                        <strong>Shared with me</strong> {`(${buildsIds?.length ?? 0})`}
                    </Text>
                </Space>
            }
        >
            <SharedWithMeBuildsList
                appId={appId}
                orgId={orgId}
                buildIds={buildsIds ?? []}
                isMobile={isMobile}
                destinationURLFactory={(buildId, bucketId) =>
                    createBuildDistroBuildRoute(orgId, appId, buildId, bucketId)
                }
                onBuildSelection={onBuildSelection}
                leftInset
            />
        </BuildBucketRowBase>
    );
};

export interface ArchivedBucketsProps extends AppComponentProps {
    buckets: BuildDistributionBucket[];
}

export const ArchivedBuckets: React.FC<ArchivedBucketsProps> = ({ buckets, appId }) => {
    const isMobile = useIsMobile();
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, appId).appImageUrl);
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    return (
        <BuildBucketRowBase
            isCollapsible={true}
            rowContent={
                <Space>
                    <DeleteOutlined style={{ color: antGrey.grey6, fontSize: 25, width: 52 }} />
                    <Text
                        style={{
                            fontSize: 16,
                            fontWeight: 400,
                            color: antGrey.grey8,
                        }}
                        ellipsis
                    >
                        <strong>Archive</strong>
                    </Text>
                </Space>
            }
        >
            <List
                style={{
                    marginLeft: isMobile ? 12 : 34,
                }}
                locale={{
                    emptyText: 'No archived buckets.',
                }}
                dataSource={buckets ?? []}
                split
                renderItem={(bucket) => (
                    <List.Item>
                        <Space split={<Divider type="vertical" />}>
                            <BuildBucketAvatar appImageUrl={appImageUrl} bucket={bucket} size={28} />

                            <Text
                                style={{
                                    fontSize: 14,
                                    fontWeight: 400,
                                    color: antGrey.grey8,
                                }}
                                ellipsis
                            >
                                {bucket.name}
                            </Text>

                            <Text
                                style={{
                                    fontSize: 14,
                                    color: antGrey.grey6,
                                }}
                                ellipsis
                            >
                                archived{' '}
                                {formatDateWithTimezone(
                                    bucket.archivedAt,
                                    'shortWithComma',
                                    effectiveTimezone,
                                    use24HourTimeFormat,
                                )}
                            </Text>
                        </Space>
                    </List.Item>
                )}
            />
        </BuildBucketRowBase>
    );
};

export interface AllBuildsListProps extends AppComponentProps {
    buckets: BuildDistributionBucket[];
    setSelectedBuildId: (buildId: string) => void;
}

export const AllBuildsList: React.FC<AllBuildsListProps> = ({ buckets, appId, orgId, setSelectedBuildId }) => {
    const isMobile = useIsMobile();

    const buildIds = buckets.reduce((acc, bucket) => {
        if (bucket.recentBuildIds) {
            acc.push(...bucket.recentBuildIds);
        }
        return acc;
    }, [] as string[]);

    return (
        <BucketBuildsList
            appId={appId}
            orgId={orgId}
            onBuildSelection={(buildId) => {
                setSelectedBuildId(buildId);
            }}
            buildIds={buildIds}
            isMobile={isMobile}
            shouldShowBucketType
            destinationURLFactory={(buildId) => createBuildDistroBuildRoute(orgId, appId, buildId)}
        />
    );
};

export const AppBuilds: React.FC<AppComponentProps> = (props) => {
    const { orgId, appId } = props;

    const history = useHistory();
    const dispatch = useDispatch();

    const location = useLocation();

    const routeParams = useParams<BucketRouteComponentMatchParams>(); // TO-DO: can likely remove this once wider mobile routes improvement is implemented (see below)
    const searchParams = new URLSearchParams(location.search);

    const buildId = searchParams.get('buildId');

    // temporary stopgap "fix" to allow desktop BD build links to work when opened directly on mobile; fixes "invalid bucket id" error and related bugs.
    // TO-DO: properly refactor our FE routes to create consistency between desktop and mobile BD urls; plus, create new mobile bd bucket builds view.
    // https://linear.app/runway-org/issue/RUN-10685
    const bucketId = searchParams.get('bucketId') ?? routeParams?.bucketIdParam;

    const [selectedBuildId, setSelectedBuildId] = React.useState(buildId);
    const [selectedBucketId, setSelectedBucketId] = React.useState(bucketId);
    const [selectedSegment, setSelectedSegment] = React.useState(SegmentOptions.BuildBuckets);
    const [isFocusedOnSearch, setIsFocusedOnSearch] = React.useState(false);
    const [searchedForBuilds, setSearchedForBuilds] = React.useState([]);
    const searchRef = React.useRef(null);
    const [searchText, setSearchText] = React.useState('');
    const [isLoadingSearchBuilds, setIsLoadingSearchBuilds] = React.useState(false);
    const [isCreateBucketModalVisible, setIsCreateBucketModalVisible] = React.useState(false);

    const { appImageUrl } = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, appId);
        return {
            appImageUrl: appSettings.appImageUrl,
            platform: appSettings.platform,
        };
    }, shallowEqual);

    const recommendedIntegrationCapabilities = [IntegrationCapability.BuildSystem];

    const incompleteCapabilities = useSelector((state: RootState) =>
        selectIncompleteCapabilitiesFromCapabilities(state, appId, recommendedIntegrationCapabilities),
    );

    const buildSystemIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.BuildSystem),
    );

    const vcsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.VersionControl),
    );

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const isLoadingBuildDistributionBuckets = useIsLoading(
        FetchableEntityType.BuildDistributionBucketsWithRecentBuilds,
    );
    const errorBuildDistributionBuckets = useError(FetchableEntityType.BuildDistributionBucketsWithRecentBuilds);

    const rawBuckets = useSelector((state: RootState) => selectBuildDistributionBucketsForAppId(state, props.appId));
    const sharedWithMeBuildIds = useSelector((state: RootState) => selectSharedWithMeBuildIds(state, props.appId));

    const devices = useSelector((state: RootState) => selectUserDevices(state));
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, orgId));
    const shouldHideRegisterDevicesPrompt = useSelector(
        (state: RootState) => selectAppSettings(state, appId).shouldHideRegisterDevicePrompt,
    );

    const anyBucketArtifactsRequireDeviceRegistration =
        rawBuckets?.some((bucket) =>
            bucket.artifactTypes?.some((artifactType) => shouldShowRegisterDevicesPromptForArtifactType(artifactType)),
        ) ?? false;
    const archivedBuckets = rawBuckets?.filter((bucket) => bucket.status === BucketStatus.Archived) ?? [];
    const buckets = rawBuckets?.filter((bucket) => bucket.status === BucketStatus.Active).sort(buildBucketSort) ?? [];

    const showShowRegisterDevicesPrompt =
        useSelector((state: RootState) => selectShouldShowRegisterDevicesPrompt(state, devices, appPlatform, orgId)) &&
        anyBucketArtifactsRequireDeviceRegistration &&
        !shouldHideRegisterDevicesPrompt;

    const userId = useSelector((state: RootState) => state.user.id);
    const isUserAdmin = useSelector((state: RootState) => selectUserIsAdmin(state));
    const shouldShowSimplifiedBuildUI = useSelector((state: RootState) => selectShouldShowSimplifiedBdUIForUser(state));
    const isUpdatingSimplifiedBuildUISetting = useIsLoading(UserUpdatable.UPDATE_SHOW_SIMPLIFIED_BD_UI, userId);

    const onSelectedSegmentChange = (value: SegmentOptions) => {
        setSelectedSegment(value);
    };

    const debouncedSearch = React.useCallback(
        debounce((value) => {
            setIsLoadingSearchBuilds(true);

            searchForBuildDistributionBuilds({ query: value, appId, orgId }, authCredentials)
                .then((builds) => {
                    const availableBuilds = builds.filter((build) => {
                        return buckets.some((bucket) => bucket.id === build.bucketId);
                    });
                    setSearchedForBuilds(availableBuilds);
                    setIsLoadingSearchBuilds(false);
                })
                .catch((error) => {
                    setSearchedForBuilds([]);
                    setIsLoadingSearchBuilds(false);
                    globalMessage.error(error.message);
                });
        }, 330),
        [authCredentials],
    );

    React.useEffect(() => {
        if (!searchText) {
            setSearchedForBuilds([]);
            return;
        }

        debouncedSearch(searchText);
    }, [searchText, appId, orgId]);

    React.useEffect(() => {
        if (errorBuildDistributionBuckets) {
            globalMessage.error(errorBuildDistributionBuckets);
        }
    }, [errorBuildDistributionBuckets]);

    React.useEffect(() => {
        if (searchRef?.current && isFocusedOnSearch && isMobile) {
            searchRef.current.focus();
        }
    }, [searchRef.current, isFocusedOnSearch]);

    React.useEffect(() => {
        if (rawBuckets?.length === 0) {
            return;
        }

        const areAllBucketsWithoutRecentBuildIds = rawBuckets?.every((bucket) => !bucket.recentBuildIds);

        if (appId && orgId && areAllBucketsWithoutRecentBuildIds) {
            dispatch(
                tryFetchEntity(FetchableEntityType.BuildDistributionBucketsWithRecentBuilds, {
                    orgId: props.orgId,
                    appId: props.appId,
                }),
            );
        }
    }, [appId, orgId]);

    React.useEffect(() => {
        dispatch(
            trackBuildDistroViewEvent({
                orgId,
                appId,
                eventName: AnalyticEvents.Event.BuildDistroViewRecentBuilds,
            }),
        );
    }, [appId, orgId]);

    const isMobile = useIsMobile();

    const shouldShowSegmentedControl = (!isFocusedOnSearch && isMobile) || (!isMobile && !searchText);

    const title =
        searchText ? null
        : selectedSegment === SegmentOptions.BuildBuckets ? 'Buckets'
        : 'Builds';

    return (
        <DesktopAndMobileHeader
            orgId={orgId}
            appId={appId}
            headerComponent={!isMobile && showShowRegisterDevicesPrompt && <RegisterDeviceAlert />}
            title={title}
            isLoading={isLoadingBuildDistributionBuckets}
            topRightComponent={
                <Space size={18}>
                    {isMobile && !isFocusedOnSearch && !isDemoInstance && (
                        <Button icon={<SearchOutlined />} onClick={() => setIsFocusedOnSearch(true)} />
                    )}
                    {!(isMobile && !isFocusedOnSearch) && !isDemoInstance && (
                        <Search
                            placeholder="Search builds"
                            allowClear
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                            }}
                            onFocus={() => setIsFocusedOnSearch(true)}
                            onBlur={() => setIsFocusedOnSearch(false)}
                            ref={searchRef}
                            style={{ width: isMobile ? 200 : 300 }}
                        />
                    )}
                    {!isMobile && !searchText && selectedSegment === SegmentOptions.BuildBuckets && (
                        <Button icon={<PlusOutlined />} onClick={() => setIsCreateBucketModalVisible(true)}>
                            Add a bucket
                        </Button>
                    )}
                    {shouldShowSegmentedControl && (
                        <div
                            style={{
                                width: isMobile ? 80 : 340,
                            }}
                        >
                            {/* @ts-ignore */}
                            <Segmented
                                block
                                value={selectedSegment}
                                options={Object.values(SegmentOptions).map((value) => ({
                                    value,
                                    label: isMobile ? '' : value,
                                    icon: iconForSegment(value, isMobile, value === selectedSegment),
                                }))}
                                onChange={(value) => onSelectedSegmentChange(value as SegmentOptions)}
                            />
                        </div>
                    )}
                    {!isMobile && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <p style={{ marginBottom: 0, marginRight: '8px' }}>Show Details</p>
                            <Switch
                                loading={isUpdatingSimplifiedBuildUISetting}
                                checked={!shouldShowSimplifiedBuildUI}
                                onChange={(enabled, _) =>
                                    dispatch(tryUpdateShouldShowSimplifiedBdUI({ enabled: !enabled, userId, orgId }))
                                }
                            />
                        </div>
                    )}
                </Space>
            }
        >
            <>
                {incompleteCapabilities.length > 0 && !isMobile && isUserAdmin && (
                    <ConnectIntegrationsBannerWrapper>
                        <List
                            itemLayout="vertical"
                            dataSource={incompleteCapabilities}
                            renderItem={(capability) => {
                                const docsNodeForCapability = <DefaultDocsLinkNode capability={capability} />;
                                return (
                                    <CapabilityIntegrationsListItem
                                        appId={appId}
                                        orgId={orgId}
                                        capability={capability}
                                        onClickConnect={
                                            capability !== IntegrationCapability.BuildSystem ?
                                                null
                                            :   (defaultRoute) => {
                                                    if (capability === IntegrationCapability.BuildSystem) {
                                                        history.push(
                                                            `${defaultRoute}&${CALLER_CONTEXT_PARAM}=${SetupStepsCallerContext.BuildDistroConnectCICDIntegration}`,
                                                        );
                                                    }
                                                }
                                        }
                                        description={buildDistroDescriptionForCapability(capability)}
                                        docsNodeForCapability={docsNodeForCapability}
                                    />
                                );
                            }}
                        />
                    </ConnectIntegrationsBannerWrapper>
                )}
                {selectedSegment === SegmentOptions.BuildBuckets && !searchText ?
                    <>
                        <Space
                            direction="vertical"
                            style={{
                                width: '100%',
                            }}
                            size={24}
                        >
                            <Space
                                direction="vertical"
                                style={{ width: '100%' }}
                                size={isMobile ? 0 : 8}
                                split={
                                    isMobile && (
                                        <Divider
                                            type="horizontal"
                                            style={{
                                                margin: 0,
                                            }}
                                        />
                                    )
                                }
                            >
                                {buckets.map((bucket) => (
                                    <BuildBucketRow
                                        key={bucket.id}
                                        buildBucket={bucket}
                                        appId={appId}
                                        orgId={orgId}
                                        vcsIntegrationType={vcsIntegration?.id}
                                        buildSystemIntegration={buildSystemIntegration}
                                        imageUrl={appImageUrl}
                                        setSelectedBuildId={(buildId) => {
                                            setSelectedBuildId(buildId);
                                            setSelectedBucketId(bucket.id);
                                        }}
                                    />
                                ))}

                                <SharedWithMeBucket
                                    buildsIds={sharedWithMeBuildIds}
                                    appId={appId}
                                    orgId={orgId}
                                    onBuildSelection={() => {
                                        dispatch(
                                            trackBuildDistroViewEvent({
                                                orgId,
                                                appId,
                                                eventName: AnalyticEvents.Event.BuildDistroViewSharedWithMe,
                                            }),
                                        );
                                    }}
                                />

                                <ArchivedBuckets buckets={archivedBuckets} appId={appId} orgId={orgId} />
                            </Space>
                        </Space>
                        <CreateEditBuildBucketModal
                            open={isCreateBucketModalVisible}
                            onCancel={() => setIsCreateBucketModalVisible(false)}
                            appId={appId}
                            orgId={orgId}
                        />
                    </>
                : selectedSegment === SegmentOptions.MostRecent && !searchText ?
                    <AllBuildsList
                        buckets={buckets}
                        appId={appId}
                        orgId={orgId}
                        setSelectedBuildId={setSelectedBuildId}
                    />
                :   <BuildsList
                        appId={appId}
                        orgId={orgId}
                        builds={searchedForBuilds}
                        isLoading={isLoadingSearchBuilds}
                        isMobile={isMobile}
                        shouldShowBucketType
                        setSelectedBuildId={(buildId) => {
                            const bucketId = searchedForBuilds.find((build) => build.id === buildId)?.bucketId;

                            const url = createBuildDistroBuildRoute(orgId, appId, buildId, bucketId);
                            history.push(url);
                        }}
                    />
                }
                {selectedBuildId && (
                    <BuildDetailsDrawer
                        buildId={selectedBuildId}
                        bucketId={selectedBucketId}
                        buildSystemIntegrationType={buildSystemIntegration?.id}
                        onCancel={() => {
                            setSelectedBuildId(null);
                            setSelectedBucketId(null);
                        }}
                        afterOpenChange={(open) => {
                            if (!open) {
                                const url = new URL(`${window.location.origin}${createAppBuildsRoute(orgId, appId)}`);
                                window.history.pushState({}, '', url);
                            }
                        }}
                        appId={appId}
                        orgId={orgId}
                    />
                )}
            </>
        </DesktopAndMobileHeader>
    );
};

const buildDistroDescriptionForCapability = (capability: IntegrationCapability) => {
    switch (capability) {
        case IntegrationCapability.BuildSystem:
            return 'Link buckets to specific workflows to automatically pull build artifacts for distribution, create new builds, and more.';
        case IntegrationCapability.AppStore:
            return 'See provisioning and signing details to understand who can install which builds.';
        case IntegrationCapability.Notifications:
            return 'Get notified when new builds become available, share installation links via DM, and more.';
        default:
            return null;
    }
};
